import QuantumDataAnalyzer from './QuantumDataAnalyzer';
import quantumEngine from './QuantumEngine';
import { formatRealityLayer } from './RealityLayerFormatter';
import { formatDilatedTime } from './DilatedTimeFormatter';

// Month abbreviation to number mapping
const MONTHS = {
  JAN: '01', FEB: '02', MAR: '03', APR: '04', MAY: '05', JUN: '06',
  JUL: '07', AUG: '08', SEP: '09', OCT: '10', NOV: '11', DEC: '12'
};

class QuantumAnalysisService {
  constructor() {
    // Initialize quantum analysis system
    this.analyzer = new QuantumDataAnalyzer();

    // Analysis history
    this.analysisHistory = [];

    // Current quantum metrics
    this.quantumMetrics = quantumEngine.getCurrentState();

    // Subscribe to quantum engine updates
    this.unsubscribe = quantumEngine.subscribe(metrics => {
      this.quantumMetrics = metrics;
    });
  }

  // Update quantum metrics (for backward compatibility)
  updateQuantumMetrics(metrics) {
    this.quantumMetrics = metrics;
  }

  async analyzeQuantumData() {
    try {
      // Analyze quantum signature using stored metrics
      const analysis = this.analyzer.analyzeQuantumSignature(this.quantumMetrics);
      
      // Create analysis report
      const report = this.createAnalysisReport(analysis);
      
      // Store in history
      this.analysisHistory.push({
        timestamp: new Date().toISOString(),
        metrics: this.quantumMetrics,
        analysis: analysis
      });

      // Maintain history size
      if (this.analysisHistory.length > 100) {
        this.analysisHistory.shift();
      }

      return report;

    } catch (error) {
      console.error('Quantum analysis error:', error);
      return this.createErrorReport(error);
    }
  }

  createAnalysisReport(analysis) {
    const {
      coherenceAnalysis,
      frequencySpectrum,
      entanglementMetrics,
      phaseCharacteristics,
      anomalyDetection,
      decodedSignature,
      particleProperties,
      cosmicPhenomena
    } = analysis;

    // Calculate signal quality and qubit reward probability
    const signalQuality = this.determineSignalQuality(analysis);
    const qubitRewardChance = this.calculateQubitRewardChance(signalQuality, analysis);

    // Determine signal classification
    const signalClass = this.classifySignal(analysis);

    // Simplified anomaly summary
    const anomalySummary = this.summarizeAnomalies(anomalyDetection.anomalies);

    // Get reality layer identifier
    const realityLayerId = this.quantumMetrics.realityLayer?.toString() || 'UNKNOWN';

    return {
      signalSummary: {
        class: signalClass,
        strength: parseFloat(decodedSignature.signalStrength).toFixed(1),
        stability: parseFloat(decodedSignature.quantumStability).toFixed(1),
        quality: parseFloat(signalQuality).toFixed(1)
      },
      quantumState: {
        coherence: this.formatPercentage(coherenceAnalysis.stabilityIndex),
        entanglement: this.formatPercentage(entanglementMetrics.correlationStrength),
        resonance: this.formatPercentage(decodedSignature.resonancePattern.amplitude * 100)
      },
      particleActivity: particleProperties ? {
        type: particleProperties.particleType.type,
        energy: this.formatScientific(particleProperties.particleType.energy),
        confidence: this.formatPercentage(particleProperties.particleType.confidence)
      } : null,
      cosmicEvents: cosmicPhenomena?.astronomicalEvents?.events.length > 0 ? {
        count: cosmicPhenomena.astronomicalEvents.events.length,
        types: cosmicPhenomena.astronomicalEvents.events.map(e => e.type),
        significance: this.formatPercentage(parseFloat(cosmicPhenomena.astronomicalEvents.totalIntensity) * 100)
      } : null,
      rewards: {
        qubitChance: this.formatPercentage(qubitRewardChance),
        multiplier: this.calculateRewardMultiplier(analysis),
        rarity: this.determineSignalRarity(analysis)
      },
      alerts: anomalySummary,
      metadata: {
        timestamp: (() => {
          // Get the dilated time for this reality layer
          const layerState = quantumEngine.generateInitialState(parseInt(realityLayerId));
          const timeDilation = layerState.metrics.timeDilation;
          const dilatedTime = formatDilatedTime(timeDilation);
          // Parse the dilated date and time into ISO string
          const [day, monthAbbr, year] = dilatedTime.date.split(' ');
          const [time, period] = dilatedTime.time.split(' ');
          const [hours, minutes, secondsWithMs] = time.split(':');
          const seconds = secondsWithMs.split('.')[0];
          
          // Convert 12-hour to 24-hour format
          let hour24 = parseInt(hours);
          if (period === 'PM' && hour24 !== 12) hour24 += 12;
          if (period === 'AM' && hour24 === 12) hour24 = 0;
          
          const month = MONTHS[monthAbbr];
          const date = new Date(`${year}-${month}-${day}T${String(hour24).padStart(2, '0')}:${minutes}:${seconds}.000Z`);
          return date.toISOString();
        })(),
        readingId: this.generateReadingId(),
        realityLayer: realityLayerId
      }
    };
  }

  determineSignalQuality(analysis) {
    const {
      coherenceAnalysis,
      entanglementMetrics,
      phaseCharacteristics,
      frequencySpectrum,
      decodedSignature,
      particleProperties,
      cosmicPhenomena
    } = analysis;
    
    // Core quantum metrics with decoded signature
    const quantumFactors = [
      coherenceAnalysis.stabilityIndex / 100,
      parseInt(entanglementMetrics.correlationStrength) / 100,
      phaseCharacteristics.phaseStability / 100,
      parseFloat(decodedSignature.signalStrength) / 100,
      parseFloat(decodedSignature.quantumStability) / 100,
      parseFloat(decodedSignature.resonancePattern.amplitude)
    ];
    
    // Particle metrics
    const particleFactors = particleProperties ? [
      parseFloat(particleProperties.particleType.confidence) / 100,
      parseFloat(particleProperties.spinState.probability),
      1 - parseFloat(particleProperties.tunneling.probability)
    ] : [0.5, 0.5, 0.5];
    
    // Cosmic metrics
    const cosmicFactors = cosmicPhenomena ? [
      cosmicPhenomena.pulsarAnalysis.detected ? parseFloat(cosmicPhenomena.pulsarAnalysis.signalStrength) : 0.5,
      parseFloat(cosmicPhenomena.gravitationalWaves.confidence),
      parseFloat(cosmicPhenomena.backgroundRadiation.fluctuation)
    ] : [0.5, 0.5, 0.5];
    
    // Weighted contribution from each category
    const weights = {
      quantum: [2.0, 2.0, 2.0, 1.5, 1.5, 1.5],  // Higher weight for core quantum metrics
      particle: [1.0, 1.0, 1.0],                 // Medium weight for particle data
      cosmic: [1.0, 1.0, 1.0]                    // Medium weight for cosmic data
    };
    
    // Calculate weighted sums for each category
    const quantumScore = this.calculateWeightedScore(quantumFactors, weights.quantum);
    const particleScore = this.calculateWeightedScore(particleFactors, weights.particle);
    const cosmicScore = this.calculateWeightedScore(cosmicFactors, weights.cosmic);
    
    // Combine scores with category weights
    const totalScore = (
      quantumScore * 0.5 +    // 50% quantum metrics
      particleScore * 0.25 +  // 25% particle metrics
      cosmicScore * 0.25      // 25% cosmic metrics
    );
    
    // Convert to 0-100 scale and ensure valid range with 1 decimal precision
    return Math.min(100, Math.max(0, parseFloat((totalScore * 100).toFixed(1))));
  }

  calculateQubitRewardChance(signalQuality, analysis) {
    const {
      decodedSignature,
      anomalyDetection,
      particleProperties,
      cosmicPhenomena
    } = analysis;

    // Base chance from signal quality
    let baseChance = signalQuality / 200; // 0-50% base chance

    // Bonus from anomalies
    const anomalyBonus = anomalyDetection.count * 0.05; // +5% per anomaly

    // Bonus from particle energy
    const particleBonus = particleProperties ?
      Math.min(0.2, parseFloat(particleProperties.particleType.confidence) / 500) : 0;

    // Bonus from cosmic events
    const cosmicBonus = cosmicPhenomena?.astronomicalEvents?.events.length ?
      Math.min(0.15, cosmicPhenomena.astronomicalEvents.events.length * 0.03) : 0;

    // Calculate total chance with bonuses
    const totalChance = Math.min(0.95, baseChance + anomalyBonus + particleBonus + cosmicBonus);
    
    return totalChance * 100; // Convert to percentage
  }

  calculateRewardMultiplier(analysis) {
    const { decodedSignature, anomalyDetection } = analysis;
    
    // Base multiplier from signal strength
    const baseMultiplier = 1 + (parseFloat(decodedSignature.signalStrength) / 100);
    
    // Bonus multiplier from anomalies
    const anomalyMultiplier = 1 + (anomalyDetection.count * 0.2);
    
    // Combined multiplier
    return Math.round((baseMultiplier * anomalyMultiplier) * 10) / 10;
  }

  determineSignalRarity(analysis) {
    const {
      decodedSignature,
      anomalyDetection,
      particleProperties,
      cosmicPhenomena
    } = analysis;

    // Calculate rarity score based on multiple factors
    let rarityScore = 0;

    // Signal strength contribution
    rarityScore += parseFloat(decodedSignature.signalStrength) / 20;

    // Anomaly contribution
    rarityScore += anomalyDetection.count * 15;

    // Particle properties contribution
    if (particleProperties?.particleType?.type === 'exotic') rarityScore += 30;
    if (particleProperties?.spinState?.state === 'superposition') rarityScore += 20;

    // Cosmic phenomena contribution
    if (cosmicPhenomena?.gravitationalWaves?.detected) rarityScore += 25;
    if (cosmicPhenomena?.pulsarAnalysis?.detected) rarityScore += 20;

    // Determine rarity tier
    if (rarityScore >= 90) return 'LEGENDARY';
    if (rarityScore >= 70) return 'EPIC';
    if (rarityScore >= 50) return 'RARE';
    if (rarityScore >= 30) return 'UNCOMMON';
    return 'COMMON';
  }

  classifySignal(analysis) {
    const {
      decodedSignature,
      frequencySpectrum,
      particleProperties
    } = analysis;

    // Get primary characteristics
    const strength = parseFloat(decodedSignature.signalStrength);
    const stability = parseFloat(decodedSignature.quantumStability);
    const band = frequencySpectrum.frequencyBand;
    const particleType = particleProperties?.particleType?.type;

    // Determine signal class based on characteristics
    if (strength > 90 && stability > 80) return 'PRISTINE';
    if (strength > 70 && particleType === 'exotic') return 'EXOTIC';
    if (band === 'High-Band' && stability > 60) return 'HIGH_ENERGY';
    if (stability < 30) return 'UNSTABLE';
    return 'STANDARD';
  }

  summarizeAnomalies(anomalies) {
    if (!anomalies.length) return [];

    return anomalies.map(anomaly => ({
      type: anomaly.type,
      severity: anomaly.significance,
      bonus: this.getAnomalyBonus(anomaly.type)
    }));
  }

  getAnomalyBonus(type) {
    const bonusMap = {
      'High Coherence': '+15% Qubit Chance',
      'Strong Entanglement': '+20% Qubit Chance',
      'Phase Instability': '+10% Multiplier',
      'Gravitational Wave': '+25% Multiplier',
      'Multiple Cosmic Events': '+30% Qubit Chance',
      'Pulsar Signal': '+20% Multiplier'
    };
    return bonusMap[type] || '+5% Qubit Chance';
  }

  formatPercentage(value) {
    // Maintain 1 decimal place precision for better accuracy
    return parseFloat(value).toFixed(1) + '%';
  }

  formatScientific(value) {
    return parseFloat(value).toExponential(2);
  }

  calculateWeightedScore(factors, weights) {
    if (!factors || !weights || factors.length !== weights.length) {
      return 0.5; // Return default mid-range score for invalid input
    }
    const weightedSum = factors.reduce((sum, factor, i) => {
      const validFactor = isNaN(factor) ? 0.5 : factor; // Use mid-range value for invalid factors
      return sum + validFactor * weights[i];
    }, 0);
    const totalWeight = weights.reduce((a, b) => a + b, 0);
    return totalWeight > 0 ? weightedSum / totalWeight : 0.5;
  }

  generateReadingId() {
    return 'QR-' + Date.now().toString(36).toUpperCase();
  }

  createErrorReport(error) {
    return {
      signalSummary: {
        class: 'UNSTABLE',
        strength: 0,
        stability: 0,
        quality: 0
      },
      quantumState: {
        coherence: '0%',
        entanglement: '0%',
        resonance: '0%'
      },
      rewards: {
        qubitChance: '0%',
        multiplier: '1.0x',
        rarity: 'COMMON'
      },
      particleActivity: null,
      cosmicEvents: null,
      alerts: [],
      metadata: {
        timestamp: new Date().toISOString(),
        readingId: this.generateReadingId(),
        realityLayer: 'ERROR',
        error: error.message || 'Unknown analysis error'
      }
    };
  }

  getAnalysisHistory() {
    return this.analysisHistory;
  }

  clearHistory() {
    this.analysisHistory = [];
  }
}

// Create single instance
const quantumAnalysisService = new QuantumAnalysisService();
export default quantumAnalysisService;